"use client";

import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Scissors,
  ArrowUp,
  FileText,
  Sparkles,
  CheckCircle2,
  Target,
  Hash,
} from "lucide-react";

/**
 * Gemeinsame 3D-Stage:
 * Der *Hintergrund* (das gekippte 3D-Bodenraster, Lichtkegel, Partikel) erzeugt volle
 * räumliche Tiefe, während alle *Text-Karten* im Vordergrund OHNE translateZ / rotateX
 * gestochen scharf (Subpixel-Antialiasing!) gerendert werden.
 */
function Stage3D({
  children,
  glowColor = "rgba(168,85,247,0.28)",
  secondaryGlow = "rgba(16,185,129,0.22)",
}: {
  children: React.ReactNode;
  glowColor?: string;
  secondaryGlow?: string;
}) {
  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl select-none"
      style={{ perspective: "900px" }}
    >
      {/* 1. Tiefes Ambient-Nebula-Licht */}
      <motion.div
        animate={{
          scale: [1, 1.22, 1],
          opacity: [0.55, 0.85, 0.55],
        }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -top-6 left-1/2 h-44 w-56 -translate-x-1/2 rounded-full blur-3xl"
        style={{ background: glowColor }}
      />
      <motion.div
        animate={{
          scale: [1.1, 0.92, 1.1],
          opacity: [0.35, 0.7, 0.35],
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -bottom-8 left-1/2 h-36 w-48 -translate-x-1/2 rounded-full blur-3xl"
        style={{ background: secondaryGlow }}
      />

      {/* 2. Gekipptes 3D-Holo-Bodenraster (nur der Boden ist 3D-gekippt!) */}
      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(168,85,247,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(168,85,247,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* 3. Schwebende Lichtpartikel */}
      <motion.span
        animate={{ y: [-5, 7, -5], opacity: [0.25, 0.85, 0.25] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute left-8 top-6 h-1.5 w-1.5 rounded-full bg-purple-400 shadow-[0_0_8px_#a855f7]"
      />
      <motion.span
        animate={{ y: [7, -7, 7], opacity: [0.3, 0.9, 0.3] }}
        transition={{ duration: 4.1, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute right-9 bottom-8 h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_#10b981]"
      />

      {/* 4. Gestochen scharfer Vordergrund (kein preserve-3d / kein translateZ auf Text!) */}
      <div className="relative z-10 flex h-full w-full items-center justify-center antialiased">
        {children}
      </div>
    </div>
  );
}

/**
 * 1. SEMANTIC SEARCH
 */
export function SemanticSearchPreview() {
  const [matched, setMatched] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setMatched((v) => !v), 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <Stage3D>
      <div className="relative flex flex-col items-center gap-2.5">
        {/* Schwebende Such-Kapsel */}
        <motion.div
          animate={{ y: matched ? -2 : 2 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
          className="flex items-center gap-2 rounded-full border border-purple-400/60 bg-white px-4 py-1.5 shadow-[0_12px_28px_-6px_rgba(168,85,247,0.45)] dark:bg-zinc-900"
        >
          <Search className="h-3.5 w-3.5 text-purple-500" />
          <span className="font-sans text-xs font-extrabold tracking-tight text-neutral-900 dark:text-white">
            „Auto“
          </span>
        </motion.div>

        {/* Volumetrischer Hologramm-Lichtkegel */}
        <div className="relative flex h-6 w-36 items-center justify-center">
          <motion.div
            animate={{
              opacity: matched ? 0.9 : 0.35,
            }}
            transition={{ duration: 0.6 }}
            className="h-7 w-28 bg-gradient-to-b from-purple-500/45 via-emerald-400/25 to-transparent"
            style={{
              clipPath: "polygon(35% 0%, 65% 0%, 100% 100%, 0% 100%)",
            }}
          />
        </div>

        {/* Zwei Karten (gestochen scharf, Tiefe über Schatten & Y-Parallaxe) */}
        <div className="flex items-center gap-3">
          <motion.div
            animate={{
              y: matched ? -4 : 2,
            }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className={`relative flex items-center gap-2 rounded-2xl border bg-white px-3.5 py-2 transition-all duration-500 dark:bg-zinc-900 ${
              matched
                ? "border-emerald-400/80 shadow-[0_16px_32px_-4px_rgba(16,185,129,0.55)]"
                : "border-purple-500/25 shadow-md"
            }`}
          >
            <span className="text-base">🚗</span>
            <div className="flex flex-col">
              <span className="font-sans text-xs font-black text-neutral-900 dark:text-white">
                PKW
              </span>
              <span className="font-mono text-[9px] font-bold text-emerald-600 dark:text-emerald-400">
                98% Match
              </span>
            </div>
          </motion.div>

          <motion.div
            animate={{
              opacity: matched ? 0.45 : 0.8,
              y: matched ? 4 : 0,
            }}
            transition={{ type: "spring", stiffness: 220, damping: 20 }}
            className="flex items-center gap-1.5 rounded-2xl border border-black/10 bg-white/80 px-3 py-2 text-xs text-neutral-400 line-through dark:border-white/10 dark:bg-zinc-900/70"
          >
            <span>🎰</span>
            <span className="font-semibold">Automat</span>
          </motion.div>
        </div>
      </div>
    </Stage3D>
  );
}

/**
 * 2. EMBEDDING MODELS
 */
export function EmbeddingModelsPreview() {
  const [merged, setMerged] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setMerged((v) => !v), 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <Stage3D>
      {/* 3D geneigte Orbit-Ringe im Hintergrund */}
      <motion.div
        animate={{ rotateZ: 360 }}
        transition={{ duration: 16, repeat: Infinity, ease: "linear" }}
        style={{ transform: "rotateX(58deg)" }}
        className="pointer-events-none absolute h-36 w-36 rounded-full border border-dashed border-purple-400/55 shadow-[0_0_25px_rgba(168,85,247,0.25)]"
      />
      <motion.div
        animate={{ rotateZ: -360 }}
        transition={{ duration: 11, repeat: Infinity, ease: "linear" }}
        style={{ transform: "rotateX(58deg)" }}
        className="pointer-events-none absolute h-20 w-20 rounded-full border border-emerald-400/55"
      />

      {/* Leuchtender Gravitations-Kern */}
      <motion.div
        animate={{
          scale: merged ? [1, 1.35, 1.15] : 0.7,
          opacity: merged ? 1 : 0.4,
        }}
        transition={{ duration: 0.7 }}
        className="pointer-events-none absolute h-14 w-14 rounded-full bg-gradient-to-tr from-purple-500 via-fuchsia-400 to-emerald-400 blur-md"
      />

      {/* Knoten 1: 🇩🇪 Hund (gestochen scharf, gerade ausgerichtet) */}
      <motion.div
        animate={{
          x: merged ? -28 : -66,
          y: merged ? -10 : -28,
        }}
        transition={{ type: "spring", stiffness: 220, damping: 20 }}
        className="absolute z-20 flex items-center gap-1.5 rounded-full border border-purple-400/70 bg-white px-3 py-1 text-xs font-extrabold shadow-[0_10px_25px_-4px_rgba(168,85,247,0.5)] dark:bg-zinc-900"
      >
        <span>🇩🇪</span>
        <span className="text-neutral-900 dark:text-white">Hund</span>
      </motion.div>

      {/* Knoten 2: 🇬🇧 Dog */}
      <motion.div
        animate={{
          x: merged ? 28 : 66,
          y: merged ? 10 : 28,
        }}
        transition={{ type: "spring", stiffness: 220, damping: 20 }}
        className="absolute z-20 flex items-center gap-1.5 rounded-full border border-emerald-400/70 bg-white px-3 py-1 text-xs font-extrabold shadow-[0_10px_25px_-4px_rgba(16,185,129,0.5)] dark:bg-zinc-900"
      >
        <span>🇬🇧</span>
        <span className="text-neutral-900 dark:text-white">Dog</span>
      </motion.div>

      {/* Ausreißer: 🍌 Banane */}
      <motion.div
        animate={{
          x: merged ? 84 : 66,
          y: merged ? -52 : -42,
          opacity: merged ? 0.5 : 0.8,
        }}
        transition={{ type: "spring", stiffness: 180, damping: 20 }}
        className="absolute flex items-center gap-1 rounded-full border border-black/10 bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-neutral-500 shadow-sm dark:border-white/10 dark:bg-zinc-900/80 dark:text-neutral-400"
      >
        <span>🍌</span>
        <span>Banane</span>
      </motion.div>

      {/* Kopplungs-Badge im Zentrum */}
      <AnimatePresence>
        {merged && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="z-30 rounded-full border border-white/40 bg-gradient-to-r from-purple-600 to-emerald-500 px-2.5 py-0.5 font-mono text-[10px] font-black text-white shadow-[0_0_20px_rgba(16,185,129,0.8)]"
          >
            = 0.99
          </motion.div>
        )}
      </AnimatePresence>
    </Stage3D>
  );
}

/**
 * 3. CHUNKING
 * Isometrische Tiefenwirkung durch versetztes Gleiten (X/Y) + leuchtende Laser-Schnitte,
 * während die Schrift zu 100% horizontal und messerscharf bleibt!
 */
export function ChunkingPreview() {
  const [sliced, setSliced] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setSliced((v) => !v), 2500);
    return () => clearInterval(id);
  }, []);

  const slabs = [
    {
      title: "§ 1 Kontext",
      accent:
        "border-purple-400/80 shadow-[0_12px_26px_-6px_rgba(168,85,247,0.5)]",
      dot: "bg-purple-500",
      shiftX: -8,
    },
    {
      title: "§ 2 Warnung!",
      accent:
        "border-amber-400/80 shadow-[0_12px_26px_-6px_rgba(245,158,11,0.5)]",
      dot: "bg-amber-500",
      shiftX: 8,
    },
    {
      title: "§ 3 Lösung",
      accent:
        "border-emerald-400/80 shadow-[0_12px_26px_-6px_rgba(16,185,129,0.5)]",
      dot: "bg-emerald-500",
      shiftX: -4,
    },
  ];

  return (
    <Stage3D>
      <div className="relative flex w-48 flex-col items-center">
        {slabs.map((slab, i) => (
          <React.Fragment key={slab.title}>
            {i > 0 && (
              <motion.div
                animate={{
                  height: sliced ? 10 : 0,
                  opacity: sliced ? 1 : 0,
                }}
                className="relative flex w-full items-center justify-center"
              >
                <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-fuchsia-400 to-transparent shadow-[0_0_12px_#e879f9]" />
                {i === 1 && sliced && (
                  <Scissors className="absolute -right-4 h-3.5 w-3.5 text-fuchsia-400" />
                )}
              </motion.div>
            )}

            <motion.div
              animate={{
                x: sliced ? slab.shiftX : 0,
              }}
              transition={{ type: "spring", stiffness: 240, damping: 22 }}
              className={`flex w-full items-center justify-between border bg-white px-3.5 py-2 transition-all duration-300 dark:bg-zinc-900 ${
                sliced
                  ? `rounded-xl ${slab.accent}`
                  : i === 0
                    ? "rounded-t-xl border-black/15 dark:border-white/15"
                    : i === 2
                      ? "rounded-b-xl border-black/15 dark:border-white/15"
                      : "border-x border-black/15 dark:border-white/15"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${slab.dot}`} />
                <span className="font-sans text-xs font-extrabold text-neutral-900 dark:text-white">
                  {slab.title}
                </span>
              </div>
              <div className="flex gap-1">
                <span className="h-1.5 w-5 rounded-full bg-neutral-300 dark:bg-neutral-700" />
                <span className="h-1.5 w-3 rounded-full bg-neutral-200 dark:bg-neutral-800" />
              </div>
            </motion.div>
          </React.Fragment>
        ))}
      </div>
    </Stage3D>
  );
}

/**
 * 4. HYBRID SEARCH
 */
export function HybridSearchPreview() {
  const [fused, setFused] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setFused((v) => !v), 2400);
    return () => clearInterval(id);
  }, []);

  return (
    <Stage3D
      glowColor="rgba(14,165,233,0.28)"
      secondaryGlow="rgba(168,85,247,0.28)"
    >
      <div className="relative flex flex-col items-center gap-2">
        <div className="flex items-center gap-5">
          <motion.div
            animate={{ x: fused ? 6 : -4 }}
            className="flex items-center gap-1 rounded-xl border border-sky-400/70 bg-white px-2.5 py-1 font-mono text-[11px] font-black text-sky-600 shadow-[0_8px_20px_-4px_rgba(14,165,233,0.45)] dark:bg-zinc-900 dark:text-sky-300"
          >
            <Hash className="h-3 w-3" />
            <span>ERR-42</span>
          </motion.div>

          <motion.div
            animate={{ x: fused ? -6 : 4 }}
            className="flex items-center gap-1 rounded-xl border border-purple-400/70 bg-white px-2.5 py-1 font-sans text-[11px] font-black text-purple-600 shadow-[0_8px_20px_-4px_rgba(168,85,247,0.45)] dark:bg-zinc-900 dark:text-purple-300"
          >
            <Sparkles className="h-3 w-3" />
            <span>„Sensor tot“</span>
          </motion.div>
        </div>

        {/* Laser-Beams + rotierendes Kristall-Prisma */}
        <div className="relative flex h-11 w-48 items-center justify-center">
          <svg
            viewBox="0 0 180 44"
            className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          >
            <motion.path
              d="M 40 0 L 90 22"
              stroke="#0ea5e9"
              strokeWidth="2.5"
              strokeLinecap="round"
              animate={{ opacity: fused ? 1 : 0.4 }}
            />
            <motion.path
              d="M 140 0 L 90 22"
              stroke="#a855f7"
              strokeWidth="2.5"
              strokeLinecap="round"
              animate={{ opacity: fused ? 1 : 0.4 }}
            />
            <motion.path
              d="M 90 22 L 90 44"
              stroke="#10b981"
              strokeWidth="3.5"
              strokeLinecap="round"
              animate={{ opacity: fused ? 1 : 0.25 }}
            />
          </svg>

          <motion.div
            animate={{ rotate: fused ? 225 : 45 }}
            transition={{ type: "spring", stiffness: 220, damping: 16 }}
            className="z-10 h-5 w-5 rounded-md border border-white/60 bg-gradient-to-br from-sky-400 via-purple-500 to-emerald-400 shadow-[0_0_20px_rgba(168,85,247,0.8)]"
          />
        </div>

        <motion.div
          animate={{ y: fused ? -2 : 2 }}
          transition={{ type: "spring", stiffness: 280, damping: 20 }}
          className={`flex items-center gap-2 rounded-2xl border bg-white px-4 py-1.5 dark:bg-zinc-900 ${
            fused
              ? "border-emerald-400/80 shadow-[0_14px_30px_-4px_rgba(16,185,129,0.55)]"
              : "border-black/10 dark:border-white/10"
          }`}
        >
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <span className="font-sans text-xs font-black text-neutral-900 dark:text-white">
            100% Volltreffer
          </span>
        </motion.div>
      </div>
    </Stage3D>
  );
}

/**
 * 5. VECTOR DATABASES
 * 60fps 3D-Wireframe-Kubus + gestochen scharfes Badge im Vordergrund.
 */
export function VectorDatabasesPreview() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let angleY = 0.4;
    let angleX = 0.35;

    const cubeVerts = [
      [-1, -1, -1],
      [1, -1, -1],
      [1, 1, -1],
      [-1, 1, -1],
      [-1, -1, 1],
      [1, -1, 1],
      [1, 1, 1],
      [-1, 1, 1],
    ];
    const cubeEdges = [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
      [4, 5],
      [5, 6],
      [6, 7],
      [7, 4],
      [0, 4],
      [1, 5],
      [2, 6],
      [3, 7],
    ];

    const dataNodes = [
      { pos: [0.0, 0.0, 0.0], role: "query" },
      { pos: [0.35, 0.28, 0.22], role: "near" },
      { pos: [-0.32, 0.38, -0.18], role: "near" },
      { pos: [0.22, -0.4, 0.3], role: "near" },
      { pos: [-0.65, -0.55, 0.5], role: "far" },
      { pos: [0.68, -0.5, -0.55], role: "far" },
      { pos: [-0.55, 0.6, 0.55], role: "far" },
    ];

    const project = (x: number, y: number, z: number, w: number, h: number) => {
      const cosY = Math.cos(angleY);
      const sinY = Math.sin(angleY);
      const x1 = x * cosY - z * sinY;
      const z1 = z * cosY + x * sinY;

      const cosX = Math.cos(angleX);
      const sinX = Math.sin(angleX);
      const y2 = y * cosX - z1 * sinX;
      const z2 = z1 * cosX + y * sinX;

      const fov = 3.2;
      const scale = (Math.min(w, h) * 0.36 * fov) / (fov + z2);
      return {
        px: w / 2 + x1 * scale,
        py: h / 2 + y2 * scale,
      };
    };

    const render = (time: number) => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (
        canvas.width !== rect.width * dpr ||
        canvas.height !== rect.height * dpr
      ) {
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
      }
      ctx.save();
      ctx.scale(dpr, dpr);
      const w = rect.width;
      const h = rect.height;

      ctx.clearRect(0, 0, w, h);

      angleY += 0.012;
      angleX = 0.38 + Math.sin(time * 0.0012) * 0.15;

      const projectedCube = cubeVerts.map(([x, y, z]) =>
        project(x, y, z, w, h)
      );
      ctx.strokeStyle = "rgba(168, 85, 247, 0.28)";
      ctx.lineWidth = 1.2;
      for (const [a, b] of cubeEdges) {
        ctx.beginPath();
        ctx.moveTo(projectedCube[a].px, projectedCube[a].py);
        ctx.lineTo(projectedCube[b].px, projectedCube[b].py);
        ctx.stroke();
      }

      const projectedNodes = dataNodes.map((n) => ({
        ...n,
        ...project(n.pos[0], n.pos[1], n.pos[2], w, h),
      }));

      const queryNode = projectedNodes[0];
      const pulse = (Math.sin(time * 0.004) + 1) / 2;

      ctx.beginPath();
      ctx.arc(queryNode.px, queryNode.py, 10 + pulse * 26, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(16, 185, 129, ${0.55 - pulse * 0.45})`;
      ctx.lineWidth = 1.8;
      ctx.stroke();

      for (const node of projectedNodes) {
        if (node.role === "near") {
          ctx.beginPath();
          ctx.moveTo(queryNode.px, queryNode.py);
          ctx.lineTo(node.px, node.py);
          ctx.strokeStyle = "rgba(16, 185, 129, 0.85)";
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }

      for (const node of projectedNodes) {
        ctx.beginPath();
        const r = node.role === "query" ? 6 : node.role === "near" ? 5 : 3.2;
        ctx.arc(node.px, node.py, r, 0, Math.PI * 2);
        if (node.role === "query") {
          ctx.fillStyle = "#a855f7";
          ctx.shadowColor = "#a855f7";
          ctx.shadowBlur = 12;
        } else if (node.role === "near") {
          ctx.fillStyle = "#10b981";
          ctx.shadowColor = "#10b981";
          ctx.shadowBlur = 10;
        } else {
          ctx.fillStyle = "rgba(148, 163, 184, 0.5)";
          ctx.shadowBlur = 0;
        }
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      ctx.restore();
      animId = requestAnimationFrame(render);
    };

    animId = requestAnimationFrame(render);
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <Stage3D>
      <canvas ref={canvasRef} className="h-full w-full" />
      <div className="pointer-events-none absolute bottom-2.5 rounded-full border border-emerald-500/50 bg-white px-3 py-0.5 font-mono text-[10px] font-extrabold text-emerald-700 shadow-md dark:bg-zinc-900 dark:text-emerald-300">
        3D Top-K Nachbarn ✓
      </div>
    </Stage3D>
  );
}

/**
 * 6. RERANKING
 */
export function RerankingPreview() {
  const [reranked, setReranked] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setReranked((v) => !v), 2500);
    return () => clearInterval(id);
  }, []);

  const items = reranked
    ? [
        { id: "best", text: "Exakte Lösung", score: "0.99", best: true },
        { id: "meh1", text: "Allgemeines Intro", score: "0.58", best: false },
        { id: "meh2", text: "Alte Notiz", score: "0.34", best: false },
      ]
    : [
        { id: "meh1", text: "Allgemeines Intro", score: "0.74", best: false },
        { id: "meh2", text: "Alte Notiz", score: "0.71", best: false },
        { id: "best", text: "Exakte Lösung", score: "0.68", best: true },
      ];

  return (
    <Stage3D>
      <div className="flex w-full max-w-[235px] flex-col gap-2 px-3">
        {items.map((item, index) => {
          const isWinner = item.best && reranked;
          return (
            <motion.div
              key={item.id}
              layout
              animate={{
                x: isWinner ? -4 : 0,
              }}
              transition={{ type: "spring", stiffness: 280, damping: 24 }}
              className={`flex items-center justify-between rounded-xl border bg-white px-3.5 py-2 dark:bg-zinc-900 ${
                isWinner
                  ? "border-emerald-400/80 shadow-[0_16px_32px_-6px_rgba(16,185,129,0.55)]"
                  : item.best
                    ? "border-amber-400/70 shadow-md"
                    : "border-black/10 opacity-65 dark:border-white/10"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-md font-mono text-[10px] font-black ${
                    isWinner
                      ? "bg-emerald-500 text-white shadow-[0_0_10px_#10b981]"
                      : "bg-neutral-200/80 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400"
                  }`}
                >
                  #{index + 1}
                </span>
                <span className="font-sans text-xs font-extrabold text-neutral-900 dark:text-white">
                  {item.text}
                </span>
              </div>

              <div className="flex items-center gap-1">
                {isWinner && <ArrowUp className="h-3.5 w-3.5 text-emerald-500" />}
                <span
                  className={`font-mono text-[10px] font-black ${
                    isWinner
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-neutral-400"
                  }`}
                >
                  {item.score}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </Stage3D>
  );
}

/**
 * 7. RAG
 */
export function RagPreview() {
  const [grounded, setGrounded] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setGrounded((v) => !v), 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <Stage3D>
      <div className="flex flex-col items-center gap-2.5">
        <motion.div
          animate={{ y: grounded ? -2 : 2 }}
          className="flex items-center gap-2 rounded-xl border border-purple-400/70 bg-white px-3.5 py-1.5 shadow-[0_12px_25px_-6px_rgba(168,85,247,0.45)] dark:bg-zinc-900"
        >
          <FileText className="h-4 w-4 text-purple-500" />
          <span className="font-sans text-xs font-extrabold text-neutral-900 dark:text-white">
            Handbuch.pdf
          </span>
          <span className="rounded-md bg-purple-600 px-1.5 py-0.5 font-mono text-[10px] font-black text-white">
            S. 14
          </span>
        </motion.div>

        <motion.div
          animate={{ opacity: grounded ? 1 : 0.3 }}
          className="h-4 w-1 rounded-full bg-gradient-to-b from-purple-500 to-emerald-400 shadow-[0_0_12px_#10b981]"
        />

        <motion.div
          animate={{ y: grounded ? -2 : 2 }}
          className={`flex items-center gap-2.5 rounded-2xl border bg-white px-4 py-2.5 transition-shadow duration-500 dark:bg-zinc-900 ${
            grounded
              ? "border-emerald-400/80 shadow-[0_16px_32px_-6px_rgba(16,185,129,0.55)]"
              : "border-amber-400/50 shadow-md"
          }`}
        >
          <span className="font-sans text-xs font-bold text-neutral-900 dark:text-white">
            „Max. 3,5 Bar Druck“
          </span>

          <AnimatePresence mode="wait">
            {grounded ? (
              <motion.span
                key="verified"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="rounded-lg bg-emerald-500 px-2 py-0.5 font-mono text-[10px] font-black text-white shadow-[0_0_14px_rgba(16,185,129,0.75)]"
              >
                S. 14 ✓
              </motion.span>
            ) : (
              <motion.span
                key="unverified"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="rounded-lg bg-amber-500/20 px-2 py-0.5 font-mono text-[10px] font-bold text-amber-600 dark:text-amber-400"
              >
                Quelle?
              </motion.span>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </Stage3D>
  );
}

/**
 * 8. RETRIEVAL EVALUATION
 */
export function RetrievalEvaluationPreview() {
  const [calibrated, setCalibrated] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setCalibrated((v) => !v), 2400);
    return () => clearInterval(id);
  }, []);

  const shots = calibrated
    ? [
        { x: 66, y: 57 },
        { x: 74, y: 62 },
        { x: 70, y: 64 },
      ]
    : [
        { x: 28, y: 28 },
        { x: 112, y: 90 },
        { x: 38, y: 95 },
      ];

  return (
    <Stage3D>
      <div className="flex items-center gap-4">
        {/* Nur die Grafik-Zielscheibe ist leicht 3D-geneigt */}
        <motion.div
          animate={{
            rotateX: 22,
            rotateY: calibrated ? -12 : 12,
          }}
          transition={{ type: "spring", stiffness: 180, damping: 20 }}
          className="relative h-28 w-28 shrink-0"
        >
          <svg
            viewBox="0 0 140 120"
            className="h-full w-full overflow-visible drop-shadow-[0_12px_20px_rgba(168,85,247,0.35)]"
          >
            <circle
              cx="70"
              cy="60"
              r="48"
              fill="rgba(15, 23, 42, 0.35)"
              stroke="#a855f7"
              strokeWidth="1.5"
              strokeOpacity="0.45"
            />
            <circle
              cx="70"
              cy="60"
              r="30"
              fill="none"
              stroke="#a855f7"
              strokeWidth="1.5"
              strokeDasharray="4 3"
              strokeOpacity="0.7"
            />
            <circle
              cx="70"
              cy="60"
              r="13"
              fill={
                calibrated
                  ? "rgba(16, 185, 129, 0.35)"
                  : "rgba(244, 63, 94, 0.2)"
              }
              stroke={calibrated ? "#10b981" : "#f43f5e"}
              strokeWidth="2.5"
            />

            {calibrated && (
              <motion.circle
                cx="70"
                cy="60"
                initial={{ r: 12, opacity: 0.9 }}
                animate={{ r: 44, opacity: 0 }}
                transition={{ duration: 1.4, repeat: Infinity }}
                fill="none"
                stroke="#10b981"
                strokeWidth="2"
              />
            )}

            {shots.map((s, idx) => (
              <motion.circle
                key={idx}
                r="5"
                fill={calibrated ? "#10b981" : "#f43f5e"}
                animate={{ cx: s.x, cy: s.y }}
                transition={{ type: "spring", stiffness: 240, damping: 18 }}
              />
            ))}
          </svg>
        </motion.div>

        {/* Das Score-Display steht 100% aufrecht und gestochen scharf */}
        <motion.div
          animate={{ y: calibrated ? -2 : 2 }}
          className="flex flex-col items-start rounded-2xl border border-black/10 bg-white px-3.5 py-2 shadow-xl dark:border-white/15 dark:bg-zinc-900"
        >
          <span className="flex items-center gap-1 font-mono text-[9px] font-bold uppercase tracking-wider text-purple-500">
            <Target className="h-3 w-3" />
            Präzision
          </span>
          <span
            className={`font-mono text-2xl font-black tabular-nums ${
              calibrated ? "text-emerald-500" : "text-rose-500"
            }`}
          >
            {calibrated ? "98%" : "41%"}
          </span>
          <span className="text-[10px] font-bold text-neutral-500 dark:text-neutral-400">
            {calibrated ? "Bullseye ✓" : "Streut..."}
          </span>
        </motion.div>
      </div>
    </Stage3D>
  );
}
